import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import { type FieldValues, type Path, type UseFormReturn, useFormContext } from 'react-hook-form';
import type { LiteralUnion } from 'type-fest';

type SectionConfig = {
  hidden?: boolean;
  contentOnly?: boolean;
};

type FormConfig = {
  noActions?: boolean;
  noWrapper?: boolean;
};

export type RHFNestedFormProviderProps<
  TFieldValues extends FieldValues = FieldValues,
  SectionNameUnion extends string = string,
> = {
  namespace?: Path<TFieldValues>;
  /** If this configuration object is empty, all sections are enabled and shown as usual.
   * If this configuration object contains at least one entry, sections which ware not included in the configuration are non-displayed */
  sections?: Partial<Record<LiteralUnion<SectionNameUnion, string>, SectionConfig>>;
  formConfig?: FormConfig;
};

type RHFNestedFormContextProps = {
  isSectionVisible: (name: string | undefined) => boolean;
  getSectionConfig: (name: string | undefined) => SectionConfig | undefined;
  formConfig: Required<FormConfig>;
  namespace?: string;
  isNested?: boolean;
  parentForm: null | UseFormReturn<any>;
};

const RHFNestedFormContext = createContext<RHFNestedFormContextProps>({
  isSectionVisible: () => true,
  getSectionConfig: () => undefined,
  formConfig: { noActions: false, noWrapper: false },
  parentForm: null,
  isNested: false,
});

export const useRHFNestedFormContext = () => useContext(RHFNestedFormContext) as unknown as RHFNestedFormContextProps;

export const RHFNestedFormProvider = <
  TFieldValues extends FieldValues = FieldValues,
  SectionNameUnion extends string = string,
>({
  children,
  namespace,
  sections = {},
  formConfig,
}: PropsWithChildren<RHFNestedFormProviderProps<TFieldValues, SectionNameUnion>>) => {
  const outerFormMethods = useFormContext<TFieldValues>();

  // biome-ignore-start lint/correctness/useExhaustiveDependencies: JSON.stringify is used to reduce amount of renders
  const value: RHFNestedFormContextProps = useMemo(
    () => ({
      formConfig: { noActions: false, noWrapper: false, ...formConfig },
      isSectionVisible: name => Object.keys(sections).length === 0 || !!(name && sections[name]),
      getSectionConfig: name => (name !== undefined ? sections[name] : undefined),
      namespace: namespace || '',
      isNested: !!outerFormMethods,
      parentForm: outerFormMethods || null,
    }),
    [namespace, JSON.stringify(sections), JSON.stringify(formConfig), outerFormMethods]
  );
  // biome-ignore-end lint/correctness/useExhaustiveDependencies: JSON.stringify is used to reduce amount of renders

  return <RHFNestedFormContext.Provider value={value}>{children}</RHFNestedFormContext.Provider>;
};
